import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Constants } from '../../common';
import {
  ContactUsMessageSendReplyForm,
  DashboardBalanceReportForm,
  DashboardPeakTimeReportForm,
  DataWithPagination,
} from '../../interface';
import { Mapper } from '../mapper/mapper';
import { DashboardBalanceReport, DashboardPeakTimeReport, DashboardReportsCharts } from '../model';
import { CurrentCallsChart } from '../model/current-calls-chart-model';
import { AppConsumptionModel } from '../model/app-consumption.model';
import { BaseAPI } from './base-api';
import { DashboardContactUsMessagesFilters } from 'src/app/shared/common';
import { ContactUsMessages } from '../model/contact-us-messages-model';
import { ContactUsMessageDetails } from '../model/contact-us-message-details-model';
import { DashboardChartsForm, DateFilterWithPagination } from 'src/app/shared/interfaces';

@Injectable({
  providedIn: 'root',
})
export class DashboardApi {
  constructor(private baseAPI: BaseAPI, private mapper: Mapper) {}

  getReportsCharts(form?: DashboardChartsForm): Observable<DashboardReportsCharts> {
    const params = new URLSearchParams();
    Object.entries(form).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
    return this.baseAPI
      .get(`${Constants.DASHBOARD_REPORTS_CHARTS_API_URL}?${params.toString()}`)
      .pipe(map((res) => this.mapper.fromJson(DashboardReportsCharts, res['dashBoardReport'])));
  }

  getCountOfCurrentCallsReport(category?: string, dateRange?: DateFilterWithPagination): Observable<CurrentCallsChart> {
    return this.baseAPI
      .post(Constants.DASHBOARD_COUNT_OF_CURRENT_CALLS, {
        category,
        fromDate: dateRange?.fromDate,
        toDate: dateRange?.toDate,
      })
      .pipe(
        map((res) => {
          return this.mapper.fromJson(CurrentCallsChart, res.currentCalls);
        })
      );
  }

  getBalanceReport(balanceForm: DashboardBalanceReportForm): Observable<DashboardBalanceReport> {
    return this.baseAPI.post(Constants.DASHBOARD_GET_BALANCE_REPORT_API_URL, balanceForm).pipe(
      map((res) => {
        return this.mapper.fromJson(DashboardBalanceReport, res);
      })
    );
  }

  getPeakTimeReport(peakForm: DashboardPeakTimeReportForm): Observable<DashboardPeakTimeReport> {
    return this.baseAPI.post(Constants.DASHBOARD_GET_PEAK_TIME_REPORT_API_URL, peakForm).pipe(
      map((res) => {
        return this.mapper.fromJson(DashboardPeakTimeReport, res);
      })
    );
  }

  getContactUsMessages(
    filters: DashboardContactUsMessagesFilters
  ): Observable<DataWithPagination<ContactUsMessages[]>> {
    const body = {
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      pageIndex: filters.pageIndex,
      pageSize: filters.pageSize,
      senderEmail: filters.email,
      senderName: filters.name,
      senderUsernme: filters.userName,
    };
    return this.baseAPI.post(Constants.DASHBOARD_CONTACT_US_MESSAGES_API_URL, body).pipe(
      map((res) => {
        const messages: DataWithPagination<ContactUsMessages[]> = {
          data: this.mapper.fromListJson(ContactUsMessages, res.items),
          count: res.count,
        };
        return messages;
      })
    );
  }

  getContactUsMessageDetails(messageId: string): Observable<ContactUsMessageDetails[]> {
    return this.baseAPI.post(Constants.DASHBOARD_GET_CONTACT_US_MESSAGE_DETAILS, { messageId }).pipe(
      map((res) => {
        return this.mapper.fromListJson(ContactUsMessageDetails, res.items);
      })
    );
  }

  contactUsMessageReply(formReply: ContactUsMessageSendReplyForm): Observable<string> {
    const body = { ...formReply, file: formReply.fileURL, fileURL: undefined };
    return this.baseAPI.post(Constants.DASHBOARD_SEND_REPLY_TO_MESSAGE_URL_API, body).pipe(
      map((res) => {
        return res.message;
      })
    );
  }

  getAppConsumption(): Observable<AppConsumptionModel> {
    return this.baseAPI
      .get(Constants.APP_CONSUMPTION_API_URL)
      .pipe(map((res) => this.mapper.fromJson(AppConsumptionModel, res)));
  }
}
